'use client';
import { AreaChart, ResponsiveContainer, Area } from 'recharts';
import { FloraIcon, Button } from '@practium/ui';
import styles from './ActionCard.module.css';
import type { ActionCardData } from '../../types/home.types';
import {
	ACTION_CARD_CHART_DATA,
	CHART_MARGIN,
	CHART_GRADIENT_CONFIG,
} from '../../constants/actionCard.constants';
import { getActionCardClassName } from '../../utils/getActionName';

interface ActionCardProps {
	data: ActionCardData;
	onClick?: (link: string) => void;
}

export const ActionCard = ({ data, onClick }: ActionCardProps) => {
	const handleClick = () => {
		if (onClick) {
			onClick(data.buttonLink);
		}
	};

	const renderCardIcon = () => {
		switch (data.variant) {
			case 'flora':
				return (
					<div className={styles.floraIconWrapper}>
						<FloraIcon className={styles.floraIcon} />
					</div>
				);
			case 'applications':
				return (
					<div className={styles.linearGradient}>
						<div className={styles.linearGradientInner} />
					</div>
				);
			case 'dashboard':
				return (
					<div className={styles.chartContainer}>
						<ResponsiveContainer width="100%" height="100%">
							<AreaChart data={ACTION_CARD_CHART_DATA} margin={CHART_MARGIN}>
								<defs>
									<linearGradient
										id={CHART_GRADIENT_CONFIG.id}
										x1={CHART_GRADIENT_CONFIG.x1}
										y1={CHART_GRADIENT_CONFIG.y1}
										x2={CHART_GRADIENT_CONFIG.x2}
										y2={CHART_GRADIENT_CONFIG.y2}
									>
										{CHART_GRADIENT_CONFIG.stops.map((stop) => (
											<stop
												key={`${stop.offset}-${stop.color}`}
												offset={stop.offset}
												stopColor={stop.color}
												stopOpacity={stop.opacity}
											/>
										))}
									</linearGradient>
								</defs>
								<Area
									type="monotone"
									dataKey="value"
									stroke="var(--gradient-chart-start)"
									fill={`url(#${CHART_GRADIENT_CONFIG.id})`}
								/>
							</AreaChart>
						</ResponsiveContainer>
					</div>
				);
			default:
				return null;
		}
	};

	const { baseClass, variantClass } = getActionCardClassName(styles.card, data.variant);
	const cardClassName =
		variantClass && styles[variantClass] ? `${baseClass} ${styles[variantClass]}` : baseClass;

	return (
		<div className={cardClassName}>
			<div className={styles.content}>
				<h3 className={styles.title}>{data.title}</h3>
				<p className={styles.subtitle}>{data.subtitle}</p>
				<div className={styles.buttonContainer}>
					<div>
						<Button variant="cardAction" className={styles.actionButton} onClick={handleClick}>
							{data.buttonText}
						</Button>
					</div>
					{renderCardIcon()}
				</div>
			</div>
		</div>
	);
};
